'use client'

import { useCallback, useEffect, useState } from 'react'

import { isRatingWidgetDisabled } from '@/app/lib/utils'

const STORAGE_KEY = 'voted_performances'

function readVotedPerformances(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : []
  } catch {
    return []
  }
}

interface UseVotedPerformances {
  /** `null` mientras no se ha leído `localStorage` (servidor y primer render
   * de cliente coinciden, sin *hydration mismatch*); array una vez resuelto. */
  readonly votedPerformances: readonly string[] | null
  /** Registra un voto con éxito. Solo persiste en `localStorage` si el
   * usuario dio consentimiento de cookies (`isRatingWidgetDisabled() ===
   * false`, research.md R7) — el voto en sí ya se envió al backend con
   * independencia de esto (FR-012). */
  readonly markAsVoted: (performanceId: string) => void
}

/**
 * Puente entre `localStorage.voted_performances` y el estado de React
 * (spec.md FR-014). Deliberadamente `useState<...| null>(null)` + lectura en
 * `useEffect`, nunca en el cuerpo de render: un Client Component también
 * renderiza en servidor, donde `localStorage` no existe
 * (architecture.rule.md).
 */
export function useVotedPerformances(): UseVotedPerformances {
  const [votedPerformances, setVotedPerformances] = useState<readonly string[] | null>(null)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- lectura de localStorage post-hidratación, ver architecture.rule.md
    setVotedPerformances(readVotedPerformances())
  }, [])

  const markAsVoted = useCallback((performanceId: string) => {
    setVotedPerformances((current) => {
      const next = current?.includes(performanceId) ? current : [...(current ?? []), performanceId]
      if (!isRatingWidgetDisabled()) {
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
        } catch {
          // localStorage puede lanzar en modo privado o con cuota agotada — el
          // voto ya se envió al backend igualmente (FR-012); solo se pierde el
          // registro local de "ya votado" en este navegador.
        }
      }
      return next
    })
  }, [])

  return { votedPerformances, markAsVoted }
}
